import type { DTOOf, IComment } from '@kfolks/types'
import { apiClient } from './api.client'

/**
 * Parent-scoped comments API for Request Center (per ADR-0011).
 *
 * The browser ALWAYS goes through `/requests/{requestId}/comments/*`
 * — never `/comments/*` directly (those are admin-only by design).
 * Future parent types (incidents, projects, etc.) ship their own
 * api file with the same `CommentApiClient` shape so `<CommentsPanel>`
 * stays parent-agnostic.
 */

/** Enriched comment row returned by `GET /requests/:id/comments` —
 *  the canonical Comment DTO plus the resolved author display name (so
 *  the frontend doesn't have to fan out an admin-only `/users/{id}`
 *  lookup per row). `authorDisplayName` is null when user-service can't
 *  resolve the userId (deleted user, system actor without a user row). */
export type CommentDTO = DTOOf<IComment> & { authorDisplayName?: string | null }

export interface CommentCreateBody {
	content: string
	isInternal?: boolean
	metadata?: { mentions?: string[] }
}

export interface CommentUpdateBody {
	content?: string
	isInternal?: boolean
	metadata?: { mentions?: string[] }
}

/**
 * Shape `<CommentsPanel>` consumes — every parent-type embed wires its
 * own implementation. Each method takes the parent id (`requestId` for
 * the Request Center surface) so the client can build the path.
 */
export interface CommentApiClient {
	list(parentId: string): Promise<{ data: CommentDTO[] }>
	create(parentId: string, body: CommentCreateBody): Promise<CommentDTO>
	update(parentId: string, commentId: string, body: CommentUpdateBody): Promise<CommentDTO>
	softDelete(parentId: string, commentId: string): Promise<void>
}

export const requestCommentApi: CommentApiClient = {
	async list(requestId) {
		const res = await apiClient.get<{ data: CommentDTO[] }>(`/requests/${requestId}/comments`)
		return res.data
	},
	async create(requestId, body) {
		const res = await apiClient.post<CommentDTO>(`/requests/${requestId}/comments`, body)
		return res.data
	},
	async update(requestId, commentId, body) {
		const res = await apiClient.put<CommentDTO>(`/requests/${requestId}/comments/${commentId}`, body)
		return res.data
	},
	async softDelete(requestId, commentId) {
		await apiClient.delete(`/requests/${requestId}/comments/${commentId}`)
	},
}
